import { Entity } from '@backstage/catalog-model';
import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow } from '../components/CustomCatalogTable/types'; // adjust path if needed

// Extracts the target name from a relation object.
// Falls back to parsing targetRef or returns "Unknown" if none found.
export const getTargetName = (relation: any): string =>
  relation.target?.name ||
  (typeof relation.targetRef === 'string'
    ? relation.targetRef.split('/').pop()
    : 'Unknown');

// Normalizes a string by trimming, lowercasing, and removing non-alphanumeric characters.
export const normalizeName = (name: string): string =>
  name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

// Formats a tag string into Title Case.
export const formatTag = (tag: string): string =>
  tag
    .toLowerCase()
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Filters out any data entries named 'catalog' from a Vega dataset.
export const filterCatalogData = (data: any): any[] =>
  Array.isArray(data) ? data.filter((d: any) => d.name !== 'catalog') : [];

// Gets a "rounded" maximum value for a scale based on input values.
// Adds guardrails for very small numbers and rounds to nearest 10.
export const getRoundedMax = (values: number[], minThreshold = 10): number => {
  const maxVal = Math.max(...values, 0);
  if (maxVal === 0) return 0;
  if (maxVal === 1) return 1;
  if (maxVal < minThreshold) return Math.ceil(maxVal);
  return Math.ceil(maxVal / 10) * 10;
};

// Counts how many entities match a clicked API name and are marked as "non-helios".
export const countMatchingNonHeliosEntities = (
  entities: Entity[],
  clickedData: { name: string },
): number => {
  return entities.filter(entity => {
    const entityName = entity.metadata.title || entity.metadata.name;
    const scope = entity.metadata.annotations?.['backstage.io/serviceScope']?.toLowerCase();
    return (
      entityName.toLowerCase() === clickedData.name.toLowerCase() &&
      (scope === 'non-helios' || scope === 'nonhelios')
    );
  }).length;
};

// Counts how many entities match a clicked API name and are considered "helios" scoped.
export const countMatchingHeliosEntities = (
  entities: Entity[],
  clickedData: { name: string },
): number => {
  return entities.filter(entity => {
    const entityName = entity.metadata.title || entity.metadata.name;
    const scope = entity.metadata.annotations?.['backstage.io/serviceScope']?.toLowerCase();
    return (
      entityName.toLowerCase() === clickedData.name.toLowerCase() &&
      scope !== 'non-helios' && scope !== 'nonhelios'
    );
  }).length;
};

// Extracts all unique tags across all entities.
export const getUniqueTags = (entities: Entity[]): Set<string> => {
  const tags = new Set<string>();
  
entities.forEach(entity => {
    if (Array.isArray(entity.relations)) {
        entity.relations
            .filter(relation => relation.type === 'dependencyOf')
            .forEach(relation => {
                if (relation.targetRef.split(':')[0] === 'component') {
                    const target = relation.targetRef.split(':')[1].split('/')[1];
                    if (typeof target === 'string') tags.add(target);
                }
            });
    }
});

  return tags;
};

// Hardcoded list of allowed consumer tags for adoption rate charts.
export const allowedConsumerHeliosAdoptionRateCharts: string[] = [
  'component:default/visionlink',
  'component:default/foresight',
  'component:default/sos-2-0-web-application',
  'component:default/admin-tool',
  'component:default/vision-link-productivity-non-prod',
  'component:default/cat-inspect',
  'component:default/dealer-services-portal',
  'component:default/parts-cat-com',
  'component:default/cat-central',
  // 'component:default/cat-translate'
];

// Maps consumer identifiers to human-readable display names.
export const consumerDisplayNames: Record<string, string> = {
  'component:default/visionlink': 'VisionLink',
  'component:default/foresight': 'Foresight',
  'component:default/sos-2-0-web-application': 'SOS 2.0 Web Application',
  'component:default/admin-tool': 'Admin Tool',
  'component:default/vision-link-productivity-non-prod': 'Vision Link Productivity Non-Prod',
  'component:default/cat-inspect': 'Cat Inspect',
  'component:default/dealer-services-portal': 'Dealer Services Portal',
  'component:default/parts-cat-com': 'Parts.Cat.Com',
  'component:default/cat-central':'Cat Central',
  // 'component:default/cat-translate':'Cat Translate'
};

// Styles Material Table columns with ellipsis truncation and padding.
export const getStyledColumns = (
  columns: TableColumn<CatalogTableRow>[] | undefined,
): TableColumn<CatalogTableRow>[] | undefined => {
  if (!columns) return undefined;

  return columns.map(column => ({
    ...column,
    cellStyle: {
      width: '200px',
      minWidth: '250px',
      maxWidth: '300px',
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordWrap: 'break-word',
      display: 'table-cell',
      height: '5px',
      padding: '16px 16px 14px 20px',
    },
    headerStyle: {
      width: '300px',
      minWidth: '250px',
      maxWidth: '300px',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },
  }));
};

// Applies detailed styling to specific consumer table columns.
export const getStyledConsumerDetailColumns = (
  columns: TableColumn<any>[] | undefined,
): TableColumn<any>[] | undefined => {
  if (!columns) return undefined;

  const columnWidths = [
    250, // Component
    150, // Service/API
    150, // Part of Cat Digital
    130, // Industry
    140, // Business Unit
    350, // Technical Owner
  ];

  return columns.map((column, index) => {
    const width = columnWidths[index] ?? 100;

    return {
      ...column,
      cellStyle: {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordWrap: 'break-word',
        display: 'table-cell',
        height: '5px',
        padding: '16px 16px 14px 20px',
      },
      headerStyle: {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        backgroundColor: 'white',
        fontWeight: 'bold',
        borderBottom: '1px solid rgb(218, 213, 213)',
      },
    };
  });
};

// Generates a Vega signal that allows selection via clicking bars.
export const getSelectedSignal = (initialValue: any = null): any => ({
  name: 'selected',
  value: initialValue,
  on: [
    {
      events: 'rect:click',
      update: 'selected && selected.name === datum.name ? null : datum',
      force: true,
    },
  ],
});

// Aggregates entity tags into a structure grouped by tag name.
export const getRelationsByTag = (
  entities: Entity[],
): Record<string, { count: number; relations: any[] }> => {
  const depMap = new Map<string, { count: number; relations: any[] }>();
  entities.forEach(entity => {
    // (entity.metadata.tags || []).forEach(tag => {
    (entity.relations || [])
      .filter(relation => relation.type === 'dependencyOf')
      .forEach(relation => {
        if (relation.targetRef.split(':')[0] === 'component') {
          const target = relation.targetRef.split(':')[1].split('/')[1];
          const formattedTag = formatTag(target);
      if (!depMap.has(formattedTag)) {
        depMap.set(formattedTag, { count: 0, relations: [] });
      }

      const entry = depMap.get(formattedTag)!;
      entry.count++;

      const relationEntry = {
        type: 'tag-mapped-relation',
        target: { name: formattedTag },
      };

      if (!entry.relations.some(rel => rel.target.name === formattedTag)) {
        entry.relations.push(relationEntry);
      }}
    });
  });

  return Object.fromEntries(depMap);
};

// Converts tag-based entity data into chart-friendly structure.
export const transformEntitiesToTags = (
  entities: Entity[],
): { name: string; externalConsumers: number; relations: any[] }[] => {
  const tagMap = new Map<string, { count: number; relations: any[] }>();

  entities.forEach(entity => {
    (entity.relations || [])
      .filter(relation => relation.type === 'dependencyOf')
      .forEach(relation => {
        const target = relation.targetRef.split(':')[1].split('/')[1];
        if (relation.targetRef.split(':')[0] === 'component') {
        const formattedTag = formatTag(target);

      if (!tagMap.has(formattedTag)) {
        tagMap.set(formattedTag, { count: 0, relations: [] });
      }

      const entry = tagMap.get(formattedTag)!;
      entry.count++;

      const related = {
        type: 'tag-mapped-relation',
        target: { name: formattedTag },
      };

      if (!entry.relations.some(r => r.target.name === formattedTag)) {
        entry.relations.push(related);
      }}
    });
  });

  return Array.from(tagMap.entries()).map(([name, { count, relations }]) => ({
    name,
    externalConsumers: count,
    relations,
  }));
};

// Converts APIs (entities) into a format used by the left Vega chart.
export const transformEntitiesToAPIs = (
  entities: Entity[],
): { name: string; externalConsumers: number; relations: any[] }[] => {
  return entities
    .filter(
      entity =>
        !entity.metadata.name.includes('pfm') &&
        !entity.metadata.name.includes('-db'),
    )
    .map(entity => {
      const name = entity.metadata.title || entity.metadata.name;
      const tags = (entity.relations || [])
                  .filter(relation => relation.type === 'dependencyOf')
                  .map(relation => {
                    if (relation.targetRef.split(':')[0] === 'component') {
                      return relation.targetRef.split(':')[1].split('/')[1];
                    }
                    })
                    .filter(tag => tag !== undefined)
      const relations = Array.from(
        new Set(tags.map(tag => formatTag(tag.toLowerCase()))),
      ).map(tag => ({
        type: 'tag-mapped-relation',
        target: { name: tag },
      }));

      return { name, externalConsumers: relations.length, relations };
    })
    .sort((a, b) => b.externalConsumers - a.externalConsumers);
};

// Classifies a list of entities as helios vs non-helios based on annotations.
export const countHeliosClassification = (
  entities: Entity[],
): { helios: number; nonHelios: number } => {
  let helios = 0;
  let nonHelios = 0;

  for (const entity of entities) {
    const scope = entity.metadata.annotations?.[
      'backstage.io/serviceScope'
    ]?.toLowerCase();

    if (scope === 'non-helios' || scope === 'nonhelios') {
      nonHelios++;
    } else {
      helios++;
    }
  }

  return { helios, nonHelios };
};
